import { CdkAccordionModule } from '@angular/cdk/accordion';
import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, input, OnDestroy, signal, untracked } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatSliderModule } from '@angular/material/slider';
import { VendorModel } from '@vendasta/ai-assistants';
import { GalaxyBadgeModule } from '@vendasta/galaxy/badge';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { filter, map, of, startWith, switchMap } from 'rxjs';
import { AiAssistantI18nModule } from '../../assets/i18n/ai-assistant-i18n.module';
import {
  AiVoiceConfigurationForm,
  DeepgramConfigForm,
  ElevenLabsConfigForm,
  ModelConfigForm,
  OpenAIConfigForm,
  OpenAIRealtimeConfigForm,
} from '../../core/forms';
import { getVoiceFamilies } from '../../core/services/ai-assistant-utils';
import { AiAssistantService } from '../../core/services/ai-assistant.service';
import { MatCheckboxModule } from '@angular/material/checkbox';

@Component({
  selector: 'ai-voice-configuration-form',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatSelectModule,
    MatIconModule,
    MatButtonModule,
    MatSliderModule,
    CdkAccordionModule,
    ReactiveFormsModule,
    GalaxyFormFieldModule,
    AiAssistantI18nModule,
    GalaxyBadgeModule,
    MatCheckboxModule,
  ],
  templateUrl: './ai-voice-configuration-form.component.html',
  styleUrl: './ai-voice-configuration-form.component.scss',
})
export class AiVoiceConfigurationFormComponent implements OnDestroy {
  readonly voiceConfigForm = input.required<AiVoiceConfigurationForm>();

  VendorModel = VendorModel;
  private readonly aiAssistantService = inject(AiAssistantService);

  protected readonly isPreGreetingMessageEnabled = this.aiAssistantService.isPreGreetingMessageEnabled;

  protected readonly DEFAULT_PRE_GREETING_MESSAGE = 'This call is being recorded for quality and note taking purposes.';

  protected readonly voiceFamilies = toSignal(this.aiAssistantService.voiceFeatures$.pipe(map(getVoiceFamilies)));

  private readonly voiceFamily$ = toObservable(this.voiceConfigForm).pipe(
    filter((form) => !!form?.controls?.voiceFamily),
    switchMap((form) => {
      const voiceFamilyControl = form?.controls?.voiceFamily;
      return voiceFamilyControl?.valueChanges.pipe(startWith(voiceFamilyControl?.value)) ?? of(null);
    }),
  );
  protected readonly voiceFamily = toSignal(this.voiceFamily$);

  protected readonly vendorModel = computed(() => this.voiceFamily()?.provider);

  protected readonly modelConfig = computed(() => {
    return this.getModelConfig(this.voiceConfigForm()?.controls?.modelConfig, this.vendorModel() ?? null);
  });

  private readonly voice$ = toObservable(this.modelConfig).pipe(
    filter((modelConfig) => !!modelConfig?.controls?.voice),
    switchMap((modelConfig) => {
      const voiceControl = modelConfig?.controls?.voice;
      return voiceControl?.valueChanges.pipe(startWith(voiceControl?.value)) ?? of(null);
    }),
  );
  protected readonly voice = toSignal(this.voice$);

  private readonly deprecatedVoices: Array<{ provider: VendorModel; voice: string }> = [
    { provider: VendorModel.VENDOR_MODEL_OPEN_AI_REALTIME, voice: 'sage' }
  ];

  protected readonly displayVoices = computed(() => {
    const family = this.voiceFamily();
    const currentVoice = this.voice();
    if (!family?.voices) return [];
    
    return family.voices.filter(voice => {

      if (voice.value === currentVoice) return true;
      
      const isDeprecated = this.deprecatedVoices.some(
        deprecated => deprecated.provider === family.provider && deprecated.voice === voice.value
      );
      
      return !isDeprecated;
    });
  });

  protected readonly aiVoice = computed(() => {
    return this.voiceFamily()?.voices.find((voice) => voice.value === this.voice());
  });

  protected readonly hasPreviewAudioUrl = computed(() => {
    const selectedVoice = this.aiVoice();
    return !!selectedVoice?.previewAudioUrl;
  });

  protected readonly openAITurnDetectionForm = computed(() => {
    const modelConfig = this.modelConfig();
    return this.vendorModel() === VendorModel.VENDOR_MODEL_OPEN_AI_REALTIME && 
           modelConfig instanceof OpenAIRealtimeConfigForm
      ? modelConfig.controls.turnDetection
      : null;
  });

  private readonly showPreGreetingMessage$ = toObservable(this.voiceConfigForm).pipe(
    filter((form) => !!form?.controls?.showPreGreetingMessage),
    switchMap((form) => {
      const showPreGreetingMessageControl = form?.controls?.showPreGreetingMessage;
      return showPreGreetingMessageControl?.valueChanges.pipe(startWith(showPreGreetingMessageControl?.value)) ?? of(false);
    }),
  );
  protected readonly showPreGreetingMessage = toSignal(this.showPreGreetingMessage$);

  protected readonly currentlyPlayingPreview = signal(false);
  private audioElement: HTMLAudioElement | null = null;

  private readonly previewAudio = computed(() => {
    const selectedVoice = this.aiVoice();
    return new Audio(selectedVoice?.previewAudioUrl || '');
  });

  constructor() {
    effect(() => {
      // When voice changes, stop any playing audio
      this.voice();
      untracked(() => {
        if (this.currentlyPlayingPreview()) {
          this.stopAudio();
        }
      });
    });
    effect(() => {
      // When the voice family changes
      const voiceFamily = this.voiceFamily();

      // update the voice value to a voice in that family if it isn't already
      const voiceControl = this.getModelConfig(
        this.voiceConfigForm()?.controls?.modelConfig,
        voiceFamily?.provider ?? null,
      )?.controls?.voice;

      if (voiceFamily?.voices && !voiceFamily.voices.some((aiVoice) => aiVoice.value === voiceControl?.value)) {
        const firstNonDeprecatedVoice = voiceFamily.voices.find(v => 
          !this.deprecatedVoices.some(deprecated => 
            deprecated.provider === voiceFamily.provider && deprecated.voice === v.value
          )
        );
        const newValue = firstNonDeprecatedVoice?.value || voiceFamily.voices[0]?.value || '';
        
        if (newValue && newValue !== voiceControl?.value) {
          voiceControl?.setValue(newValue);
          voiceControl?.markAsDirty();
        }
      }

      // and update the vendor model
      const vendorModelControl = this.voiceConfigForm()?.controls?.vendorModel;
      if (vendorModelControl) {
        const newValue = voiceFamily?.provider ?? null;
        if (vendorModelControl.value !== newValue) {
          vendorModelControl.setValue(newValue);
          vendorModelControl.markAsDirty();
        }
      }
    });
    effect(() => {
      // Set default pre-greeting message when checkbox is checked and field is empty
      const showPreGreetingMessage = this.showPreGreetingMessage();
      const form = this.voiceConfigForm();
      const preGreetingMessageControl = form?.controls?.preGreetingMessage;
      
      if (showPreGreetingMessage && preGreetingMessageControl) {
        const currentValue = preGreetingMessageControl.value;
        if (!currentValue || currentValue.trim() === '') {
          untracked(() => {
            preGreetingMessageControl.setValue(this.DEFAULT_PRE_GREETING_MESSAGE);
            preGreetingMessageControl.markAsDirty();
          });
        }
      }
    });
  }

  private getModelConfig(
    modelConfigForm: ModelConfigForm | undefined,
    vendorModel: VendorModel | null,
  ): OpenAIRealtimeConfigForm | DeepgramConfigForm | ElevenLabsConfigForm | OpenAIConfigForm | null {
    switch (vendorModel) {
      case VendorModel.VENDOR_MODEL_OPEN_AI_REALTIME:
        return modelConfigForm?.controls?.openAIRealtimeConfig ?? null;
      case VendorModel.VENDOR_MODEL_DEEPGRAM:
        return modelConfigForm?.controls?.deepgramConfig ?? null;
      case VendorModel.VENDOR_MODEL_ELEVEN_LABS:
        return modelConfigForm?.controls?.elevenLabsConfig ?? null;
      case VendorModel.VENDOR_MODEL_OPEN_AI:
        return modelConfigForm?.controls?.openAIConfig ?? null;
      default:
        return null;
    }
  }

  protected togglePreview(): void {
    if (this.currentlyPlayingPreview()) {
      this.stopAudio();
    } else {
      this.playAudio();
    }
  }

  private playAudio(): void {
    this.stopAudio(); // Stop any existing audio first
    this.audioElement = this.previewAudio();

    if (this.audioElement) {
      this.audioElement.onended = () => this.currentlyPlayingPreview.set(false);
      this.audioElement
        .play()
        .then(() => this.currentlyPlayingPreview.set(true))
        .catch((error) => console.error('Error playing audio:', error));
    }
  }

  private stopAudio(): void {
    if (this.audioElement) {
      this.audioElement.pause();
      this.audioElement.currentTime = 0;
      this.audioElement.onended = null;
      this.audioElement = null;
    }
    this.currentlyPlayingPreview.set(false);
  }

  ngOnDestroy(): void {
    this.stopAudio();
  }
}
